// src/features/game/components/TemplateFormFields.tsx

import React from 'react';

import type {
  CreateTemplateRequest,
} from '../types/game.types';

type TemplateFormData = Omit<
  CreateTemplateRequest,
  'blinds' | 'reEntries'
>;

type TemplateFormField =
  keyof TemplateFormData;

type TemplateFormErrors =
  Partial<
    Record<
      TemplateFormField,
      string
    >
  >;

interface TemplateFormFieldsProps {
  formData: TemplateFormData;

  onChange: (
    field: TemplateFormField,
    value: string | number,
  ) => void;

  errors: TemplateFormErrors;
}

const TemplateFormFields:
  React.FC<
    TemplateFormFieldsProps
  > = ({
    formData,
    onChange,
    errors,
  }) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* 게임명 */}
      <div className="md:col-span-2">
        <label className="mb-1 block text-sm font-medium text-gray-700">
          게임명
        </label>

        <input
          type="text"
          value={
            formData.gameName
          }
          onChange={(event) =>
            onChange(
              'gameName',
              event.target.value,
            )
          }
          placeholder="게임명을 입력하세요"
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.gameName
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.gameName && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.gameName
            }
          </p>
        )}
      </div>

      {/* 시작 스택 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          시작 스택
        </label>

        <input
          type="number"
          min={1}
          value={
            formData.gameStack
          }
          onChange={(event) =>
            onChange(
              'gameStack',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.gameStack
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.gameStack && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.gameStack
            }
          </p>
        )}
      </div>

      {/* 바이인 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          바이인 금액
        </label>

        <input
          type="number"
          min={1}
          value={
            formData.gamePrice
          }
          onChange={(event) =>
            onChange(
              'gamePrice',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.gamePrice
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.gamePrice && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.gamePrice
            }
          </p>
        )}
      </div>

      {/* 바이인 제한 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          바이인 제한
        </label>

        <input
          type="number"
          min={1}
          value={
            formData.gameLimit
          }
          onChange={(event) =>
            onChange(
              'gameLimit',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.gameLimit
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.gameLimit && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.gameLimit
            }
          </p>
        )}
      </div>

      {/* 바이인 종료 레벨 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          바이인 종료 레벨
        </label>

        <input
          type="number"
          min={1}
          value={
            formData.buyinEndLevel
          }
          onChange={(event) =>
            onChange(
              'buyinEndLevel',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.buyinEndLevel
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.buyinEndLevel && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.buyinEndLevel
            }
          </p>
        )}
      </div>

      {/* 게임 종료 레벨 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          게임 종료 레벨
        </label>

        <input
          type="number"
          min={1}
          value={
            formData.gameEndLevel
          }
          onChange={(event) =>
            onChange(
              'gameEndLevel',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.gameEndLevel
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.gameEndLevel && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.gameEndLevel
            }
          </p>
        )}
      </div>

      {/* 얼리칩 */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          얼리칩
        </label>

        <input
          type="number"
          min={0}
          value={
            formData.earlyAmount
          }
          onChange={(event) =>
            onChange(
              'earlyAmount',
              Number(
                event.target.value,
              ),
            )
          }
          className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
            errors.earlyAmount
              ? 'border-red-400'
              : 'border-gray-300 focus:border-main'
          }`}
        />

        {errors.earlyAmount && (
          <p className="mt-1 text-sm text-red-500">
            {
              errors.earlyAmount
            }
          </p>
        )}
      </div>
    </div>
  );
};

export default TemplateFormFields;